/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useRef, useState} from "react"
import {v4 as uuid} from "uuid"
import {cloneDeep, filter, indexOf, isEmpty, isEqual, pick, uniq} from "lodash"
import debounce from "debounce-promise"
import {useTranslation} from "react-i18next"
import {BaseInput} from "components/inputs"
import {BaseButton, AddItemCircleButton} from "components/buttons"
import {KlassappTable, KlassappTableHeader, KlassappTableDropdown, KlassappTableDropdownAsyncPaginate} from "uiKit"
import {KlassappTableProps} from "types/common"
import {academicIntegrationMapping, courseService} from "services"
import {handleError, scrollTableToBottom, toastError} from "helpers"
import {KlassappTableHOC} from "HOC"
import styles from "./IntegrationMapping.module.css"
import {EditIntegrationMapping} from "services/AcademicIntegrationMapping"

type ExternalSystemType = {
    id?: string
    name?: string
}

const ExternalSystemOptions: ExternalSystemType[] = [
    {id: "fame", name: "Fame"},
    {id: "genesis", name: "Genesis"}
]

function IntegrationMapping(props: KlassappTableProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [newDataIntegrationMapping, setNewDataIntegrationMapping] = useState([])
    const [oldDataIntegrationMapping, setOldDataIntegrationMapping] = useState([])
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        isLoading,
        menuActions,
        isHideMenuActions,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        onClickShowConfirmModal,
        tableRef,
        getCurrentData
    } = props
    const {t} = useTranslation(["financialAid", "admission", "common"])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            // {key: "onClickDuplicateMulti", func: this.onClickDuplicateMulti},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order])

    useEffect(() => {
        props.updateTableHeaderActions()
    }, [data])

    useEffect(() => {
        props.dispatch({data: newDataIntegrationMapping})
    }, [newDataIntegrationMapping])

    const getPageTitle = () => {
        return "Integration Mapping Course"
    }

    const getFields = () => {
        return ["Course Id", "Course Name", "Mapping Code", "External System"]
    }

    const getTableHeaderActions = () => {
        return [
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            }
        ]
    }

    const getMenuActions = () => {
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: onClickEdit
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: onClickDelete
            }
        ]
    }

    const getData = async () => {
        props.dispatch({isLoading: true})
        const params = getParams()
        try {
            const response = await academicIntegrationMapping.listIntegrationMapping(params)
            const integrationMappings = (response?.integrationMappings ?? []).map((item) => ({
                ...item,
                mappingCodeValue: item.mappingCode,
                externalSystemValue: item.externalSystem,
                externalSystem: cloneDeep(ExternalSystemOptions).find((ex) => ex.id === item.externalSystem)?.name,
                courseIdValue: item.courseId,
                courseCodeValue: item.courseCode,
                courseId: item.courseCode,
                courseNameValue: item.courseName,
                id: item.integrationMappingId
            }))
            setIsEditing(false)
            setNewDataIntegrationMapping([...integrationMappings])
            setOldDataIntegrationMapping([...integrationMappings])
            props.dispatch({total: response.total})
        } catch (e) {
            handleError(e)
        } finally {
            props.dispatch({isLoading: false})
        }
    }

    const getParams = () => {
        const params: any = {
            range: {page, pageSize},
            filter: {}
        }
        if (!isEmpty(orderField)) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order
            }
        }
        return params
    }

    const getColumns = () => {
        return [
            {
                title: "Course Id",
                field: "courseId",
                sortable: true
            },
            {
                title: "Course Name",
                field: "courseName",
                sortable: true
            },
            {
                title: "Mapping Code",
                field: "mappingCode",
                sortable: true
            },
            {
                title: "External System",
                field: "externalSystem",
                sortable: true
            }
        ]
    }

    const onClickDeleteMulti = async () => {
        try {
            props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            const checkedItems = getCurrentData().filter((item) => item.isChecked && item.integrationMappingId)
            const checkedItemIds = checkedItems.map((item) => item.integrationMappingId)
            if (checkedItemIds.length) {
                await academicIntegrationMapping.deleteIntegrationMappings(checkedItemIds)
            }
            const newData = getCurrentData().filter((item) => !item.isChecked)
            setNewDataIntegrationMapping(newData)
            setOldDataIntegrationMapping(newData)
            props.dispatch({total: newData.length})
        } catch (e) {
            handleError(e)
        } finally {
            props.dispatch({isLoading: false})
        }
    }

    const onClickEdit = (editItem) => {
        const newListData = getCurrentData().map((item) => {
            if (item.id === editItem.id) {
                return {
                    ...item,
                    id: item.integrationMappingId,
                    courseId: renderCourseCodeDropDown(item.integrationMappingId, {
                        code: item.courseCode,
                        courseId: item.courseIdValue
                    }),
                    courseName: renderCourseDropDown(item.integrationMappingId, {
                        name: item.courseName,
                        courseId: item.courseIdValue
                    }),
                    mappingCode: renderInput(
                        "mappingCode",
                        "Mapping Code",
                        item.integrationMappingId,
                        item.mappingCodeValue,
                        true
                    ),
                    externalSystem: renderExternalSystem(
                        item.integrationMappingId,
                        cloneDeep(ExternalSystemOptions).find((op) => op.id === item.externalSystemValue)
                    )
                }
            }
            return item
        })
        setIsEditing(true)
        setNewDataIntegrationMapping(cloneDeep(newListData))
    }

    const onClickDelete = async (deletedItem) => {
        try {
            props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            if (typeof deletedItem.id === "string") {
                const newData = getCurrentData().filter((item) => item.id !== deletedItem.id)
                setNewDataIntegrationMapping(newData)
                setOldDataIntegrationMapping(newData)
            } else {
                await academicIntegrationMapping.deleteIntegrationMappings([deletedItem.integrationMappingId])
                getData()
            }
        } catch (e) {
            handleError(e)
        } finally {
            props.dispatch({isLoading: false})
        }
    }

    const searchCourse = async (search = "", loadedOptions) => {
        try {
            const pageSize = 20
            const page = Math.ceil(loadedOptions.length / pageSize) + 1
            const test = getCurrentData()
            const {data, total} = await courseService.getAutocompleteCourse({
                filter: {
                    search,
                    ignoreCourseIds: getCurrentData()
                        .map((item) => item.courseIdValue)
                        .filter(Boolean)
                },
                range: {
                    page,
                    pageSize
                }
            })
            return {
                options: data,
                hasMore: loadedOptions.length < total
            }
        } catch (error) {
            return {
                options: [],
                hasMore: false
            }
        }
    }

    const onChangeCourse = (id, course) => {
        const changeDataForm = getCurrentData().map((item) => {
            if (item.id !== id) {
                return item
            }
            const newValue = {
                ...item,
                courseIdValue: course.courseId,
                courseCodeValue: course.code,
                courseId: renderCourseCodeDropDown(id, course),
                courseName: renderCourseDropDown(id, course)
            }
            return newValue
        })
        setNewDataIntegrationMapping(cloneDeep(changeDataForm))
    }

    const onChangeExternalSystem = (id, externalSystem: ExternalSystemType) => {
        const changeDataForm = getCurrentData().map((item) => {
            if (item.id !== id) {
                return item
            }
            const newValue = {
                ...item,
                externalSystemValue: externalSystem.id,
                externalSystem: renderExternalSystem(id, externalSystem)
            }
            return newValue
        })
        setNewDataIntegrationMapping(cloneDeep(changeDataForm))
    }

    const renderExternalSystem = (id, externalSystem?: ExternalSystemType) => {
        return (
            <KlassappTableDropdown
                options={ExternalSystemOptions}
                value={externalSystem}
                onChange={(value) => onChangeExternalSystem(id, value)}
                placeholder="Select External System"
                valueKey="id"
                labelKey="name"
            />
        )
    }

    const renderCourseDropDown = (id, course?: any) => {
        return (
            <KlassappTableDropdownAsyncPaginate
                value={course}
                key={id}
                isDisabled={false}
                valueKey="courseId"
                loadOptions={debounce(searchCourse, 300)}
                onChange={(value) => onChangeCourse(id, value)}
                labelKey="name"
                placeholder="Select Course Name"
            />
        )
    }

    const renderCourseCodeDropDown = (id, course?: any) => {
        return (
            <KlassappTableDropdownAsyncPaginate
                value={course}
                key={id}
                isDisabled={false}
                valueKey="courseId"
                loadOptions={debounce(searchCourse, 300)}
                onChange={(value) => onChangeCourse(id, value)}
                labelKey="code"
                placeholder="Select Course Id"
            />
        )
    }

    const onClickAddItem = () => {
        const id = uuid()
        const newItem = {
            id: id,
            courseId: renderCourseCodeDropDown(id),
            courseName: renderCourseDropDown(id),
            mappingCode: renderInput("mappingCode", "Mapping Code", id, "", true),
            externalSystem: renderExternalSystem(id)
        }
        const newData: any[] = [...data]
        newData.push(newItem)
        setNewDataIntegrationMapping(newData)
        scrollTableToBottom(tableRef)
    }

    const renderInput = (field: string, nameField: string, id: string, value: string, isEditing: boolean) => {
        if (!isEditing) {
            return (
                <div className={styles.titleWrap}>
                    <span className={styles.title}>{value}</span>
                </div>
            )
        }
        return (
            <BaseInput
                type="text"
                placeholder={nameField}
                value={value}
                onChange={(text) => onChangeInput(id, field, nameField, text)}
            />
        )
    }

    const onChangeInput = (id, field, nameField, value) => {
        const changeDataForm = getCurrentData().map((item) => {
            if (item.id !== id) {
                return item
            }
            const newValue = {
                ...item,
                [`${field}Value`]: value,
                [field]: renderInput(field, nameField, item.id, value, true)
            }
            return newValue
        })
        setNewDataIntegrationMapping([...changeDataForm])
    }

    const validateDataBeforeSubmit = () => {
        const hasError = getCurrentData().some(({courseIdValue, mappingCodeValue, externalSystemValue}) => {
            if (!courseIdValue || courseIdValue === "") {
                toastError(t("common:validation.cantEmpty", {field: "The course info"}))
                return true
            }
            if (!mappingCodeValue || mappingCodeValue === "") {
                toastError(t("common:validation.cantEmpty", {field: "The Mapping Code"}))
                return true
            }
            if (!externalSystemValue || externalSystemValue === "") {
                toastError(t("common:validation.cantEmpty", {field: "The External System"}))
                return true
            }
            return false
        })
        if (hasError) {
            return false
        }
        return true
    }

    const onClickSave = async () => {
        if (!validateDataBeforeSubmit()) {
            return
        }
        props.dispatch({isLoading: true})
        const params: EditIntegrationMapping[] = getCurrentData().map((item) => ({
            integrationMappingId: item.integrationMappingId,
            mappingCode: item.mappingCodeValue,
            externalSystem: item.externalSystemValue,
            courseId: item.courseIdValue
        }))
        try {
            await academicIntegrationMapping.editIntegrationMappings(params)
        } catch (e) {
            handleError(e)
        } finally {
            props.dispatch({isLoading: false})
            getData()
        }
    }

    const onClickCancel = () => {
        setIsEditing(false)
        setNewDataIntegrationMapping(cloneDeep(oldDataIntegrationMapping))
    }

    const compareIsChanged = () => {
        if (oldDataIntegrationMapping.length !== newDataIntegrationMapping.length) {
            return true
        }
        const dataCheckIntegrationMap = ["courseIdValue", "mappingCodeValue", "externalSystemValue"]
        let isEqualIntegrationMap = true

        for (let i = 0; i < newDataIntegrationMapping.length; i++) {
            const oldIntegrationMap = pick(oldDataIntegrationMapping[i], dataCheckIntegrationMap)
            const newIntegrationMap = pick(newDataIntegrationMapping[i], dataCheckIntegrationMap)
            if (!isEqual(oldIntegrationMap, newIntegrationMap)) {
                isEqualIntegrationMap = false
                break
            }
        }
        return !isEqualIntegrationMap
    }

    const isChanged = compareIsChanged()

    return (
        <div className={styles.wrapper}>
            {/* TODO  */}
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                menuActions={isHideMenuActions ? [] : menuActions}
                isLoading={isLoading}
                allFields={allFields}
                isShowCheckedColumn
                orderField={orderField}
                onClickRowItem={() => {}}
                tableRef={tableRef}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
            {!isLoading && <AddItemCircleButton onClick={onClickAddItem} />}
            <div className={styles.actionWrap}>
                {(isChanged || isEditing) && (
                    <BaseButton
                        title={t("common:action.cancel")}
                        variant="secondary"
                        className={styles.cancelBtn}
                        onClick={onClickCancel}
                    />
                )}
                <div>
                    <BaseButton
                        title={isSubmitting ? t("common:action.saving") : t("common:action.save")}
                        onClick={onClickSave}
                        isActive={isChanged}
                        loading={isSubmitting}
                    />
                </div>
            </div>
        </div>
    )
}

export default KlassappTableHOC(IntegrationMapping)
